/* eslint-disable @typescript-eslint/camelcase */
import React, { Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { Form as BootstrapForm, Button, Stack } from 'react-bootstrap';
import { Field as FormikField, ErrorMessage, FormikValues, useFormikContext, FieldProps } from 'formik';
import { ClientError, FormBlock, FormField } from '../../types/general';
import * as Schema from '../../types/schema';
import { DefaultBlockWrapper } from './Form';
import { FieldValidateArgs, findActiveRules, validate, ValidateArgs } from '../../app/validation';
import Text from '../Fields/Input/Text';
import Checkbox from '../../components/Fields/Input/Checkbox';
import Select from '../../components/Fields/Select/Select';
import Tree from '../../components/Fields/Tree/Tree';
import TextArea from '../../components/Fields/Input/TextArea';
import Wysiwyg from '../../components/Fields/Input/Wysiwyg';
import File from '../../components/Fields/File/File';
import Location from '../../components/Fields/Location/Location';
import { AppContext } from 'src/app/AppProvider';
import { LocaleContext } from 'src/app/LocaleProvider';

export const errorMessage = (fieldName: string, err: ClientError[]) => {
	return err
		.map((err) => {
			const { key, value, errCompareValue, maxLength } = err;
			const MESSAGES = {
				required: 'Campo obbligatorio',
				email: 'Inserire una mail valida',
				url: 'Inserire un indirizzo Internet valido',
				date: 'Data non valida',
				number: 'Inserire un numero',
				// compare
				value_is_not_equal_than: `Valore non uguale a ${errCompareValue}`,
				value_is_date_not_recent_than: `Data non è più recente di ${errCompareValue}`,
				value_is_date_not_oldest_than: `Data non è più vecchia di ${errCompareValue}`,
				value_is_number_not_greater_than: `Numero non è maggiore di ${errCompareValue}`,
				value_is_number_not_less_than: `Numero non è minore di ${errCompareValue}`,
				// text
				text_too_long: `Testo più lungo di ${maxLength} caratteri`,
				// time
				time_is_lesser_than_min: `L'orario indicato è prima delle ${errCompareValue}`,
				time_is_greater_than_max: `L'orario indicato è dopo le ${errCompareValue}`,
				// number
				number_is_lesser_than_min: `Numero più piccolo di ${errCompareValue}`,
				number_is_greater_than_max: `Numero più grande di ${errCompareValue}`,
			};
			return MESSAGES[key] || JSON.stringify(err);
		})
		.join('\r\n');
};

function Field(props: FormBlock<Schema.Field>) {
	const { values, errors } = useFormikContext<FormikValues>();
	const { onReccomended } = useContext(AppContext);
	const { locale, locales, onChangeLocale, _ } = useContext(LocaleContext);
	const { schema, options = {}, onFieldValidate } = props;
	const { BlockWrapper = DefaultBlockWrapper, editMode } = options;
	const { id, name, label, props: schemaProps } = schema;

	useEffect(() => {
		if (onReccomended && schemaProps?.reccomended) {
			onReccomended(schema);
		}
	}, [schemaProps]);

	const type = useMemo(() => {
		if (schema.fieldType === 'checkbox') return 'checkbox';
		if (schema.fieldType === 'file') return 'file';
		if (schema.fieldType === 'location') return 'location';
		if (schema.fieldType === 'select') return 'select';
		if (schema.fieldType === 'tree') return 'tree';
		if (schema.fieldType === 'textarea') return 'textarea';
		if (schema.fieldType === 'wysiwyg') return 'wysiwyg';
		return 'text';
	}, [schema]);

	const hasLabel = useMemo(() => {
		if (schema.fieldType === 'checkbox') return false;
		return true;
	}, [schema]);

	const fieldValidate = useCallback(
		(args: FieldValidateArgs) => {
			// bypass if in edit mode
			if (editMode) {
				return undefined;
			}
			// validation
			const res = validate(args);
			if (onFieldValidate) {
				const resCustom = onFieldValidate(args);
				if (resCustom) return resCustom;
			}
			if (res !== true) return res;
			return undefined;
		},
		[editMode]
	);

	return (
		<BlockWrapper id={schema.id} type='field' schema={schema}>
			<div key={'field-' + schema.id} className='Field mb-3'>
				<div>
					<Stack direction='horizontal' gap={1}>
						<div>
							{label && label.length > 0 && hasLabel && (
								<BootstrapForm.Label htmlFor={id}>{label}</BootstrapForm.Label>
							)}
						</div>
						<div className='ms-auto'>
							{schema.validation &&
							findActiveRules({ values, schema, options }).find((v) => v.rule === 'required') ? (
								<small className='feature form-text danger'>obbligatorio</small>
							) : null}
							{schemaProps && schemaProps.reccomended && (
								<small className='feature form-text warning'>{_('reccomended')}</small>
							)}
							{schemaProps && schemaProps.localized && locale && (locales || []).length > 1 && (
								<Fragment>
									<Button
										className='lang-btn'
										size='sm'
										variant='secondary'
										onClick={() => {
											if (onChangeLocale) onChangeLocale();
										}}
									>
										{locale?.label} <i className='bi bi-caret-down-fill' />
									</Button>
								</Fragment>
							)}
						</div>
					</Stack>
				</div>
				<FormikField
					className='form-control'
					name={name}
					id={id}
					validate={(value: any) =>
						fieldValidate({
							values,
							value,
							schema,
							options,
						})
					}
				>
					{(fieldProps: FieldProps) => {
						return (
							<Fragment>
								{type === 'select' && <Select {...props} />}
								{type === 'tree' && <Tree {...props} />}
								{/* type === 'switch-list' && <SwitchList {...props} /> */}
								{type === 'textarea' && <TextArea {...props} />}
								{type === 'wysiwyg' && <Wysiwyg {...props} />}
								{type === 'text' && <Text {...props} />}
								{type === 'checkbox' && <Checkbox {...props} />}
								{type === 'file' && <File {...props} />}
								{type === 'location' && <Location {...props} />}
							</Fragment>
						);
					}}
				</FormikField>
			</div>
		</BlockWrapper>
	);
}

export default Field;
